/* ═══════════════════════════════════════════════════════
   CrewCardView — the Call Sheet, rendered from a crew card SNAPSHOT.
   ═══════════════════════════════════════════════════════
   Used by the public page (nycsuite.io/card/{token}) and by the admin
   Preview inside the Suite. Takes only the allowlisted snapshot; it never
   sees a job document. Page and phone layouts (container width), Normcore
   and Cool (snap.style), screen and print (white letter page, phones and
   buttons dropped, session log lines added).

   Compatibility is by property presence, never by `v`: a v1 snapshot has no
   crewCall, no subject, no style, string gear, and crew rows without
   call/onSite/phone. All of those take defaults below. */
import React from "react";
import {
  Sheet, Letterhead, Section, Cols, Tiles, FactList, CheckList, Foot,
  fmtTime, fmtDateShort, fmtDate, splitLines, asList, STUDIO_ADDRESS, STUDIO_MAP,
} from "./sheet.jsx";

/* Crew call precedence (spec 4.1): the person's own call, else the sheet's
   crew call, else the session's start. */
const callFor = (snap, c) => c?.call || snap.crewCall || snap.callTime || "";
const onSiteOf = (c) => c?.onSite !== false;

export default function CrewCardView({ snap, recipientName = "", version = 0, deployedAt = "", preview = false }) {
  if (!snap) return null;
  const loc = snap.location || {};
  const crew = asList(snap.crew);
  const me = recipientName ? crew.find((c) => c.name === recipientName) : null;
  const onSite = crew.filter(onSiteOf);
  const offSite = crew.filter((c) => !onSiteOf(c));
  const startLabel = snap.subject && snap.subject !== "people" ? "Start" : "Talent";
  // Off-site crew (data processing, delivery) have no call time to print.
  const myCall = me && onSiteOf(me) ? fmtTime(callFor(snap, me)) : "";
  const cityStateZip = [[loc.city, loc.state].filter(Boolean).join(", "), loc.zip].filter(Boolean).join(" ");
  const mapHref = loc.isStudio
    ? STUDIO_MAP
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([loc.venue, loc.address, cityStateZip].filter(Boolean).join(", ") || loc.line || "")}`;
  const whereLine = loc.isStudio
    ? <span><b>Studio</b> · {STUDIO_ADDRESS}</span>
    : loc.venue
      ? <span><b>{loc.venue}</b>{[loc.address, cityStateZip].filter(Boolean).length ? ` · ${[loc.address, cityStateZip].filter(Boolean).join(", ")}` : ""}</span>
      : <span>{loc.line || "Location TBD"}</span>;
  const hasWhere = loc.isStudio || !!(loc.venue || loc.line);
  const transport = !loc.isStudio && snap.transport && snap.transport !== "In Studio" ? snap.transport : "";
  // Day-of contact: v2 carries the resolved string (may be "" = none); v1 has
  // only the coordinator.
  const dayOf = "clientContact" in snap ? snap.clientContact : snap.coordinator;
  const gearToPrep = [...asList(snap.equipment), ...asList(snap.lenses), ...asList(snap.additionalItems), ...asList(snap.extraGear)];
  const d = snap.deliverable || {};
  const deliverables = asList(snap.deliverables);
  const hasLegacyDeliverable = !!(d.format || d.delivery || d.turnaround);
  const noteLines = splitLines(snap.prepNotes);
  const clientRows = [
    ["Client", snap.clientName],
    ["Production", snap.production],
    ["Day-of contact", dayOf],
    ["Talent", (snap.talentInitials || snap.onSiteWithTalent)
      ? <>{snap.talentInitials ? <b>{snap.talentInitials}</b> : "Not named"}{snap.onSiteWithTalent ? `, with ${snap.onSiteWithTalent}` : ""}</>
      : ""],
    ["Files go to", snap.vendors],
  ].filter(([, v]) => v);

  return (
    <Sheet snap={snap} kind="crew">
      <Letterhead doc="Call sheet" line={[snap.jobCode, version ? `v${version}` : ""].filter(Boolean).join(" · ")} />
      <div className="conf">Internal. Talent by initials only. Do not forward.</div>
      <h1 className="date">{fmtDate(snap.date) || "Date TBD"}</h1>
      {/* The hot tile is the time the reader acts on: the crew call when
          there is one, else the talent / start time. */}
      <Tiles tiles={[
        { label: "Crew call", value: fmtTime(snap.crewCall), hot: true },
        { label: startLabel, value: fmtTime(snap.callTime) || "TBD", hot: !fmtTime(snap.crewCall) },
        { label: "Wrap", value: fmtTime(snap.wrapTime) },
      ]} />
      <div className="where">
        {whereLine}
        {hasWhere && <a className="lnk" href={mapHref} target="_blank" rel="noreferrer">Map</a>}
        {transport && <span><b>Transport</b> · {transport}</span>}
      </div>

      {asList(snap.otherSessionsToday).length > 0 && (
        <div className="also">Also today: {snap.otherSessionsToday.map((o) => [o.time, o.label].filter(Boolean).join(" ")).join(" · ")}</div>
      )}

      {me && (me.role || me.note || myCall) && (
        <div className="mine">
          <div className="k">Your call · {me.name.split(" ")[0]} · personal link only</div>
          <div><b>{[myCall, me.role].filter(Boolean).join(" · ")}{me.role || myCall ? "." : ""}</b> {me.note}</div>
        </div>
      )}

      <Cols>
        {crew.length > 0 && (
          <Section title={onSite.length ? "Crew on site" : "Crew"}>
            {onSite.map((c) => (
              <div className="row" key={c.name}>
                <span><b>{c.name}</b>{c.phone && <span className="ph">{c.phone}</span>}</span>
                <span className="r">{c.role}</span>
                <span className="t">{fmtTime(callFor(snap, c))}</span>
              </div>
            ))}
            {offSite.length > 0 && (
              <div className="off">Off site: {offSite.map((c) => [c.name, c.role].filter(Boolean).join(", ")).join("; ")}</div>
            )}
          </Section>
        )}
        {(clientRows.length > 0 || snap.confidential) && (
          <Section title="Client">
            <div className="kv">
              {clientRows.map(([k, v]) => <React.Fragment key={k}><span className="k">{k}</span><span>{v}</span></React.Fragment>)}
            </div>
            {snap.confidential && <div className="off">Client details withheld for this job. Ask Ivin.</div>}
          </Section>
        )}
      </Cols>

      {asList(snap.scope).length > 0 && (
        <Section title="Capture plan">
          <div className="plan">
            <div className="pl hd"><span></span><span>What</span><span>Gear</span><span>How</span></div>
            {snap.scope.map((s, i) => (
              <div className="pl" key={`${s.n}-${i}`}>
                <span className="n">{s.n}</span>
                <b>{s.line}</b>
                <span className="g">{asList(s.gear).join(", ")}</span>
                <span className="nt">{s.note}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {noteLines.length > 0 && <Section title="Notes"><FactList lines={noteLines} /></Section>}

      <Cols>
        {gearToPrep.length > 0 && <Section title="Gear to prep"><CheckList items={gearToPrep} ticks /></Section>}
        {asList(snap.checklist).length > 0 && <Section title="Day-of checklist"><CheckList items={snap.checklist} ticks /></Section>}
      </Cols>

      {(deliverables.length > 0 || hasLegacyDeliverable) && (
        <Section title="Deliverables">
          {deliverables.length > 0 ? (<>
            <div className="dl hd"><span>What</span><span>Files</span><span>Delivery</span><span>Turnaround</span></div>
            {deliverables.map((r, i) => (
              <div className="dl" key={i}>
                <b>{r.what}</b>
                <span className="s s1">{r.files}</span>
                <span className="s">{r.delivery}</span>
                <span className="s">{r.turnaround}</span>
              </div>
            ))}
          </>) : (<>
            <div className="dl hd three"><span>Format</span><span>Delivery</span><span>Turnaround</span></div>
            <div className="dl three">
              <span className="s s1">{d.format}</span>
              <span className="s">{d.delivery}</span>
              <span className="s">{d.turnaround}</span>
            </div>
          </>)}
        </Section>
      )}

      <Cols>
        {asList(snap.openItems).length > 0 && <Section title="Open items"><CheckList items={snap.openItems} /></Section>}
        <Section title="Session log" className="printonly">
          <div className="log"><div>Arrival</div><div>Start</div><div>Wrap</div><div className="wide">Issues</div></div>
        </Section>
      </Cols>

      {!preview && (
        <div className="ask">Questions or a conflict? Reply to the email this came with. Accept the calendar invite if you have not yet.</div>
      )}

      <Foot
        left={["New York Capture", version ? `v${version}` : "", deployedAt ? `sent ${fmtDateShort(deployedAt.slice(0, 10))}` : ""].filter(Boolean).join(" · ")}
        right={!preview ? <button type="button" onClick={() => window.print()}>Print sheet</button> : null}
      />
    </Sheet>
  );
}
